"use client";

import { useEffect, useRef } from "react";
import { styles } from "./UserListItem.style";
import { Icon } from "../../elements/Icon/Icon";
import { deleteWhite, edit, moreHoriz, refreshIcon } from "../../elements/Icon/Icon.types";
import { Button } from "../../elements/Button";
import { ButtonVariants } from "../../shared/Button";

export type UserAccountStatus = "ACTIVE" | "INVITED" | "INACTIVE";
export type UserBusinessRole = "COMPANY_ADMIN_ROLE" | "COMPANY_USER_ROLE";

export interface UserListItemProps {
  email: string;
  fullName?: string;
  accountStatus: UserAccountStatus;
  businessRole: UserBusinessRole;
  statusLabels: {
    active: string;
    invited: string;
    inactive: string;
    admin: string;
    user: string;
    resend?: string;
  };
  menuAriaLabel?: string;
  editAriaLabel?: string;
  deleteLabel?: string;
  disableDelete?: boolean;
  isMenuOpen?: boolean;
  onMenuClick?: () => void;
  onEditClick?: () => void;
  onResendClick?: () => void;
  onDeleteClick?: () => void;
  onMenuClose?: () => void;
  testSelector?: string;
}

export const UserListItem = (props: UserListItemProps) => {
  const {
    email,
    fullName,
    accountStatus,
    businessRole,
    statusLabels,
    menuAriaLabel = "User actions",
    editAriaLabel = "Edit user",
    deleteLabel = "Delete Account",
    disableDelete = false,
    isMenuOpen = false,
    onMenuClick,
    onEditClick,
    onResendClick,
    onDeleteClick,
    onMenuClose,
    testSelector,
  } = props;
  const isActive = accountStatus === "ACTIVE";
  const isInvited = accountStatus === "INVITED";
  const isAdmin = businessRole === "COMPANY_ADMIN_ROLE";

  const statusLabel = isActive ? statusLabels.active : isInvited ? statusLabels.invited : statusLabels.inactive;
  const statusVariant = isActive ? "success" : "default";
  const menuWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen || !onMenuClose) return undefined;
    // Use "click" (not "mousedown") so a click on an action that lives outside the
    // wrapper — e.g. the mobile action modal's Edit button — completes its onClick
    // before this closes the menu. With "mousedown" the target would unmount on press
    // and the click would never fire.
    const handleOutsideClick = (event: MouseEvent) => {
      if (menuWrapperRef.current && !menuWrapperRef.current.contains(event.target as Node)) {
        onMenuClose();
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [isMenuOpen, onMenuClose]);

  return (
    <div className={styles.card()} data-testid={testSelector}>
      <div className={styles.userCell()}>
        <p className={styles.email()}>{email}</p>
        {fullName && <p className={styles.name()}>{fullName}</p>}
      </div>
      <div className={styles.statusCell()}>
        <span className={styles.statusLabel(statusVariant)}>{statusLabel}</span>
        <span className={`${styles.statusLabel("info")} ${styles.roleBadgeMobile()}`}>
          {isAdmin ? statusLabels.admin : statusLabels.user}
        </span>
        {isInvited && onResendClick && statusLabels.resend && (
          <Button
            variant={ButtonVariants.Text}
            onClick={onResendClick}
            className={styles.resendLink()}
            testSelector={testSelector ? `${testSelector}-resend` : undefined}
          >
            <Icon icon={refreshIcon} />
            {statusLabels.resend}
          </Button>
        )}
      </div>
      <div className={styles.roleCell()}>
        <span className={styles.statusLabel("info")}>{isAdmin ? statusLabels.admin : statusLabels.user}</span>
      </div>
      <div className={styles.actionsCell()}>
        <Button
          variant={ButtonVariants.Text}
          onClick={onEditClick}
          className={styles.editButton()}
          ariaLabel={editAriaLabel}
          disabled={!onEditClick}
          testSelector={testSelector ? `${testSelector}-edit` : undefined}
        >
          <Icon icon={edit} />
        </Button>
        <div className={styles.menuWrapper()} ref={menuWrapperRef}>
          <Button
            variant={ButtonVariants.Text}
            onClick={onMenuClick}
            className={styles.iconButton()}
            ariaLabel={menuAriaLabel}
            disabled={!onMenuClick}
            testSelector={testSelector ? `${testSelector}-menu` : undefined}
          >
            <Icon icon={moreHoriz} />
          </Button>
          {isMenuOpen && onDeleteClick && (
            <div
              role="menu"
              className={styles.menuDropdown()}
              data-testid={testSelector ? `${testSelector}-menu-dropdown` : undefined}
            >
              <Button
                variant={ButtonVariants.Danger}
                onClick={onDeleteClick}
                className={styles.menuItem()}
                disabled={disableDelete}
                testSelector={testSelector ? `${testSelector}-menu-delete` : undefined}
              >
                <Icon icon={deleteWhite} className={styles.menuItemIcon()} />
                {deleteLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
